import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from 'react-router';
import { useDispatch } from "react-redux";
import classNames from "classnames";
import { throttle } from "underscore";

import { useTranslate } from '../../../../i18n/useTranslationModules';
import { EditorSizes, TemplateClassNames, TemplateNames } from "../../../../types/template-layouts";
import {
	ActionEvent,
	ActionTargetType,
	ActionType,
	BlProfile,
	BrandliveEvent,
	CreateVideo,
	Dictionary,
	EBroadcastTypes,
	FeatureFlagsEnum,
	HomepageVideo,
	LanguagesAbbr,
	MiscellaneousActionData,
	Session,
	SessionTypesEnum,
	SessionTypesFeatures
} from "../../../../types/working-model";
import {
	displayAddToCalendarButton,
	getTemplateClassName,
	isiOSMobile,
	isMobile,
	niceDateTimeTz
} from "../../../../utils/utils";
import { Button } from "../../../general-ui/button/button";
import { getAsset } from "../../modules/assets/asset-map";
import { useSocket } from "../../../../connection/socket";
import useLive from "../../../../utils/use-live";
import { useTypedSelector } from '../../../../store/reducers/use-typed-selector';
import { useScreenMediaQuery } from '../../../../utils/use-screen-media-query';
import Icon, { COLORS, ICONS } from '../../../general-ui/icon';
import SessionCountdown from './session-countdown';
import { useTracking } from '../../../../utils/tracking';
import useTimestampStatus, { ETimestampStatus } from '../../../../utils/use-timestamp-status';
import { OptionalComponent } from '../../../../utils/optional-component';
import { TypographyItem } from '../../../general-ui/typography-item/typography-item';
import { Tag } from '../../../general-ui/tag/tag';
import HandRaiseButton from '../fireside/raise-hand';
import FiresideFullscreenButton from '../fireside/fireside-fullscreen-button';
import { setFiresideLive } from '../../../../store/actions/event/firesides-actions';
import ErrorBoundary from "../../../../utils/error-boundary";
import { toggleChatPanel } from "../../../../store/actions/event/chat-actions";
import '../../../../scss/live-event/base/session/session-stream.scss';
import EditBannerColor from "../../../admin/create/session/editor/edit-banner-color";

const FiresidesPlayer = React.lazy(() => import('./firesides-player'));
const LiveChat = React.lazy(() => import('../live-chat/live-chat'));
const Video = React.lazy(() => import("../../modules/video/video"));
const AddToCalendarModal = React.lazy(() => import('../../modules/agenda/add-to-calendar-modal/add-to-calendar-modal'));

interface SessionStreamProps {
	session: Session;
	eventBundle: BrandliveEvent;
	isEditor?: boolean;
	playback?: string;
	languageProp?: LanguagesAbbr;
	editorSize?: string;
	isAdmin?: boolean;
	secondaryVideos?: Dictionary;
	interacted?: boolean;
	moderatorView?: boolean;
}

declare global {
	interface Document {
		mozCancelFullScreen?: () => Promise<void>;
		msExitFullscreen?: () => Promise<void>;
		webkitExitFullscreen?: () => Promise<void>;
		mozFullScreenElement?: Element;
		msFullscreenElement?: Element;
		webkitFullscreenElement?: Element;
	}

	interface HTMLElement {
		msRequestFullscreen?: () => Promise<void>;
		mozRequestFullscreen?: () => Promise<void>;
		webkitRequestFullscreen?: () => Promise<void>;
	}
}

const getHomepageVideo = (session: Session, language: LanguagesAbbr) => {
	let source;
	let homepageVideo;

	if (session.streaming_options?.single_stream) {
		if (Array.isArray(session.on_demand_video)) {
			source = (session.on_demand_video as CreateVideo[])[0]?.source;
			homepageVideo = (session.on_demand_video as HomepageVideo[])[0];
		} else {
			source = session.on_demand_video?.source;
			homepageVideo = session.on_demand_video;
		}
	} else {
		if (session.on_demand_video !== null && typeof session.on_demand_video === 'object' && !Array.isArray(session.on_demand_video)) {
			source = session.on_demand_video?.source;
			homepageVideo = session.on_demand_video;
		} else {
			source = session.on_demand_video?.find(video => video?.session_language === language)?.source ?? session.on_demand_video?.[0]?.source;
			homepageVideo = session.on_demand_video?.find(video => video?.session_language === language) as HomepageVideo ??
				session.on_demand_video?.[0] as HomepageVideo;
		}
	}

	return [source, homepageVideo];
};

const isIphone = isiOSMobile();

const SessionStream: React.FC<SessionStreamProps> = ({
	session,
	eventBundle,
	isEditor,
	playback = null,
	languageProp = 'en',
	editorSize = '',
	isAdmin = false,
	secondaryVideos,
	moderatorView
}) => {
	const { t } = useTranslate(["homepage", "landing"]);
	const dispatch = useDispatch();
	const { isLessThan1024, isLessThan640 } = useScreenMediaQuery();

	const blProfileUser = useTypedSelector(state => state.LiveEventReducer.blProfileUser);
	const loadingEventBundle = useTypedSelector(state => state.LiveEventReducer.loadingEventBundle);
	const secondaryVideo = useTypedSelector(state => state.CreateSessionReducer.secondarySessionVideo);
	const firesidesHostSessions: number[] = useTypedSelector(state => state.LiveEventReducer.firesidesHostSessions);
	const featureFlags = useTypedSelector(state => state.FeatureFlagsReducer.featureFlags);
	// hiding this behind a feature flag so we can test on real devices and roll back if there is an issue
	const attendeeInPersonModeEnabled = useTypedSelector(state => state.LiveEventReducer.eventBundle?.settings.attendeeInPersonEnabled);
	const [firesideFullscreen, setFiresideFullscreen] = useState(false);
	const [isFiresideLive, setIsFiresideLive] = useState(false);
	const [disableJoinStage, setDisableJoinStage] = useState(false);
	const [playbackUrl, setPlaybackUrl] = useState<string | null>(playback);
	const [currentlyWatching, setCurrentlyWatching] = useState<string | null>(null);
	const [hasScrolledPast, setHasScrolledPast] = useState(false);
	const [openAddToCalendar, setOpenAddToCalendar] = useState(false);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [chatClosed, setChatClosed] = useState(isMobile || isLessThan640);

	const { language }: { language: LanguagesAbbr; } = useParams();
	const template = getTemplateClassName(eventBundle.template.name);
	const startDate = SessionTypesFeatures[session.session_type].has_start_time ? niceDateTimeTz(session.timestamp ?? 0, language) : null;
	const isFireside = session.session_type === SessionTypesEnum.fireside;
	const isOnDemand = session.session_type === SessionTypesEnum.onDemand;
	const isBroadcast = session.session_type === SessionTypesEnum.broadcast;
	const isBreakoutRooms = session.session_type === SessionTypesEnum.breakoutRooms;
	const isFiresideHost = isFireside && firesidesHostSessions.includes(session.session);
	const isIFrameBroadcast = session.session_type === SessionTypesEnum.broadcast && session.broadcast_type === EBroadcastTypes.embed;
	const useiOSNativePlayer = featureFlags?.[FeatureFlagsEnum.iOS_mobile_native_player];

	// orientation can either be 0, -90, 90, or 180 https://developer.apple.com/documentation/webkitjs/domwindow/1632568-orientation.
	const isLandscapeIphone = isIphone && (window.orientation === -90 || window.orientation === 90);

	// secondary videos that are chosen in advance are set under "global" and should override the stream recordings
	// if in editor use the redux state secondary video that comes from sql, if in published event use the redis cache that gets updated upon publish
	const uploadedSecondaryVideo: string | undefined = isEditor ? secondaryVideo?.playback_url : secondaryVideos?.[`${session.uuid}-global`];
	const recordedSecondaryVideo: string | undefined = secondaryVideos?.[`${session.uuid}-${languageProp}`];
	const [secondaryUrl, setSecondaryUrl] = useState<string | undefined>(uploadedSecondaryVideo || recordedSecondaryVideo);
	const sessionName = session.title.base;
	const sessionChatEnabled = moderatorView ? !moderatorView : session.session_chat_enabled;

	const socket = useSocket(`session-${session.uuid}-${languageProp}`);

	const isLive = useLive(playbackUrl, session, featureFlags);

	const scrollContainer = useRef<HTMLDivElement | null>(null);
	const container = useRef<HTMLDivElement | null>(null);
	const contentRef = useRef<HTMLDivElement | null>(null);

	const session_type = session?.session_type;

	// update recorded urls when changing languages
	useEffect(() => {
		setSecondaryUrl(uploadedSecondaryVideo || recordedSecondaryVideo);
	}, [uploadedSecondaryVideo, recordedSecondaryVideo]);

	useEffect(() => {
		if (isLive) {
			//clear the secondaryUrl so a new recording can replace it (new url will be set with a socket message)
			setSecondaryUrl(undefined);
			if (session_type === SessionTypesEnum.fireside) {
				dispatch(setFiresideLive(Date.now()));
			}
		} else {
			//if the uploaded secondary video exists set that right away and don't wait for the recorded stream to come in
			//if in the editor and the secondary video has been cleared we want to reset the last recorded stream automatically.
			if (uploadedSecondaryVideo || isEditor) {
				setSecondaryUrl(uploadedSecondaryVideo || recordedSecondaryVideo);
			}
			if (session_type === SessionTypesEnum.fireside) {
				dispatch(setFiresideLive(null));
			}
		}
	}, [isLive, uploadedSecondaryVideo, recordedSecondaryVideo, isEditor, session_type, dispatch, languageProp]);

	useEffect(() => {
		const playerUrlListener = (data: any) => {
			setPlaybackUrl(data);
		};

		const currentlyOnPage = (data: { viewers: string | null; }) => {
			setCurrentlyWatching(data.viewers);
		};

		const setStreamReplay = ({ data }: { data: string; }) => {
			//Set secondary videos should override the recorded stream
			//if the uploadedSecondaryVideo exists its being placed immediately in a useEffect above when isLive is false.
			//otherwise, set the recorded stream when it's done processing
			if (!uploadedSecondaryVideo) {
				setSecondaryUrl(data);
			}
		};

		socket.addListener('playbackUrl', playerUrlListener);
		socket.addListener('on-page', currentlyOnPage);
		socket.addListener('replayVideoUrl', setStreamReplay);

		//get currently watching on load
		socket.getCurrentlyWatching(({ viewers }) => {
			setCurrentlyWatching(viewers);
		});

		return () => {
			socket.removeListener('playbackUrl', playerUrlListener);
			socket.removeListener('on-page', currentlyOnPage);
			socket.removeListener('replayVideoUrl', setStreamReplay);
		};
	}, [socket, session.uuid, secondaryVideos, uploadedSecondaryVideo]);

	useEffect(() => {
		if (playback) setPlaybackUrl(playback);
	}, [playback]);

	const timestampStatus = useTimestampStatus(session);

	const styleOverrides = useMemo(() => {

		if (session.video?.customizations?.backgroundColor) {
			return { backgroundColor: `var(--${session.video?.customizations?.backgroundColor})`, padding: isEditor ? 0 : undefined };
		}

		if (session.video?.customizations?.backgroundImage) {
			return { backgroundImage: `url(${session.video?.customizations?.backgroundImage})` };
		}

		return {};
	}, [session, isEditor]);

	const disableSessionThumbnails = eventBundle?.settings?.disable_session_thumbnails;
	const streamPlaceholderBGColor = session?.layout?.styling_overrides?.content?.stream_placeholder_background_color;

	const placeholderStyle = useMemo(() => {
		// if thumbnails are toggled off
		if (disableSessionThumbnails) {
			// use custom placeholder bg color if available
			if (streamPlaceholderBGColor?.length) {
				return { background: streamPlaceholderBGColor[0] };
			}
			// else just return empty because bg color will default to secondaryBackgroundColor via css
			return {};
		}

		const isFuture = session.timestamp && session.timestamp > Date.now();

		if (session.post_broadcast_thumbnail && !isFuture) {
			return { backgroundImage: `url(${session.post_broadcast_thumbnail})` };
		} else if (session.pre_broadcast_thumbnail) {
			return { backgroundImage: `url(${session.pre_broadcast_thumbnail})` };
		} else {
			const isCustomTemplate = !TemplateClassNames[eventBundle?.template.name];
			const defaultTemplateAsset = isCustomTemplate ? TemplateNames.Apollo : eventBundle?.template.name;

			return { backgroundImage: `url(${getAsset(defaultTemplateAsset, 'video_background')})` };
		}
	}, [
		streamPlaceholderBGColor,
		disableSessionThumbnails,
		session.timestamp,
		session.post_broadcast_thumbnail,
		session.pre_broadcast_thumbnail,
		eventBundle?.template.name
	]);

	useEffect(() => {
		const handleScroll = throttle(() => {
			const rect = scrollContainer.current?.getBoundingClientRect();

			if (rect) {
				const scrolledPast = Math.abs(rect.y) > rect.height;
				if (scrolledPast !== hasScrolledPast) {
					setHasScrolledPast(scrolledPast);
				}
			}
		}, 500);

		if (scrollContainer.current) {
			window.addEventListener('scroll', handleScroll);
			window.addEventListener('resize', handleScroll);
		}

		return () => {
			window.removeEventListener('scroll', handleScroll);
			window.removeEventListener('resize', handleScroll);
		};
	}, [scrollContainer, hasScrolledPast]);

	const closeAddToCalendar = useCallback(() => {
		setOpenAddToCalendar(false);
	}, []);

	const openAddToCalendarModal = useCallback(() => {
		setOpenAddToCalendar(true);
	}, []);

	// fireside and iframe broadcasts never uses chat overlay
	const forceOverlay = useMemo(() => (
		!isFireside
		&& !isIFrameBroadcast
		&& (editorSize === EditorSizes.tablet || (isLessThan1024 && !isLessThan640))
	), [isFireside, isIFrameBroadcast, editorSize, isLessThan1024, isLessThan640]);

	// so for tablet, fireside and iframe broadcasts acts like mobile
	const mobileOnly =
		isLessThan640
		|| editorSize === EditorSizes.mobile
		|| (isFireside && isLessThan1024)
		|| (isFireside && editorSize && editorSize !== EditorSizes.desktop)
		|| (isIFrameBroadcast && isLessThan1024)
		|| (isIFrameBroadcast && editorSize && editorSize !== EditorSizes.desktop)
		// only need to check landscape for iphone cause landscape on Android will force a fullscreen of the video
		|| isLandscapeIphone;

	const didEnterFullscreen = useCallback(() => {
		setIsFullscreen(true);
	}, []);

	const errorEnterFullscreen = useCallback((e: any) => {
		setIsFullscreen(false);
		console.error(e);
	}, []);

	const didExitFullscreen = useCallback(() => {
		setIsFullscreen(false);
		setHasScrolledPast(false);
	}, []);

	useEffect(() => {
		const _container = container.current;
		const handleFullscreenChange = () => {
			if (document.fullscreenElement || document.msFullscreenElement || document.mozFullScreenElement || document.webkitFullscreenElement) {
				setIsFullscreen(true);
			} else {
				setIsFullscreen(false);
			}
		};

		_container?.addEventListener('fullscreenchange', handleFullscreenChange);
		_container?.addEventListener('webkit-fullscreenchange', handleFullscreenChange);

		return () => {
			_container?.removeEventListener('fullscreenchange', handleFullscreenChange);
			_container?.removeEventListener('webkit-fullscreenchange', handleFullscreenChange);
		};
	}, [isFullscreen]);

	const { trackEvent } = useTracking();

	useEffect(() => {
		if (isEditor) return;

		const timer = setTimeout(() => {
			const { end_timestamp: session_end, timestamp: session_start } = session;
			const session_type = isLive ? SessionTypesEnum.broadcast : SessionTypesEnum.onDemand;
			const miscPayload: Pick<MiscellaneousActionData, 'attendance'> = {
				attendance: {
					session_end,
					session_start,
					session_type,
					user_session_start: Date.now(),
					event_name: eventBundle.name,
					session_name: sessionName,
					email: blProfileUser?.email ?? null
				}
			};

			trackEvent({
				action: ActionEvent.View,
				action_type: ActionType.Passive,
				target_type: ActionTargetType.Attendance,
				target_id: session.session,
				miscellaneous: JSON.stringify(miscPayload)
			});
		}, 90 * 1000); // fire after 90 seconds

		return () => clearTimeout(timer);
		// Fire only if the streaming status changes.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEditor, isLive]);

	/**
	 * Browsers haven't fully adopted the requestFullscreen API.
	 *
	 * Need to polyfill a bunch here.
	 */
	const handleFullscreen = useCallback((fullscreen: boolean) => {
		if (isIphone && useiOSNativePlayer) {
			// for iOS mobile we are using the native video player when going fullscreen, so we don't need to run the below polyfill (which iOS mobile doesn't support anyway).
			setIsFullscreen(fullscreen);
			return;
		}

		if (fullscreen) {
			if (container.current?.requestFullscreen) {
				container.current?.requestFullscreen()
					.then(didEnterFullscreen)
					.catch(errorEnterFullscreen);
			} else if (container.current?.webkitRequestFullscreen) {
				container.current?.webkitRequestFullscreen();
				didEnterFullscreen();
			} else if (container.current?.msRequestFullscreen) {
				container.current?.msRequestFullscreen()
					.then(didEnterFullscreen)
					.catch(errorEnterFullscreen);
			} else if (container.current?.mozRequestFullscreen) {
				container.current?.mozRequestFullscreen()
					.then(didEnterFullscreen)
					.catch(errorEnterFullscreen);
			}
		} else {
			if (document.exitFullscreen) {
				document.exitFullscreen()
					.then(didExitFullscreen)
					.catch(errorEnterFullscreen);
			} else if (document.webkitExitFullscreen) {
				document.webkitExitFullscreen();
				didExitFullscreen();
			} else if (document.mozCancelFullScreen) {
				document.mozCancelFullScreen()
					.then(didExitFullscreen)
					.catch(errorEnterFullscreen);
			} else if (document.msExitFullscreen) {
				document.msExitFullscreen()
					.then(didExitFullscreen)
					.catch(errorEnterFullscreen);
			}
		}
	}, [didEnterFullscreen, didExitFullscreen, errorEnterFullscreen, useiOSNativePlayer]);


	const handleChatClose = useCallback((chatClosed: boolean) => {
		dispatch(toggleChatPanel(false));
		setChatClosed(chatClosed);
	}, [dispatch]);

	useEffect(() => {
		if (isFireside && timestampStatus === ETimestampStatus.preLive) {
			setChatClosed(true);
		}
	}, [timestampStatus, isFireside]);

	const toggleChat = useCallback(() => {
		dispatch(toggleChatPanel(chatClosed));
		setChatClosed(!chatClosed);
	}, [chatClosed, dispatch]);

	const isFutureSession = session?.timestamp && session.timestamp > Date.now().valueOf();

	const showAddToCalBtn = eventBundle?.settings?.show_add_to_cal_btn;
	const showAddToCalendarButton = useMemo(() => {
		if (!session || moderatorView) return false;
		return displayAddToCalendarButton(session, showAddToCalBtn);
	}, [moderatorView, session, showAddToCalBtn]);

	const displayCurrentlyWatching = session?.layout?.display_viewer_count;

	const broadcastUrl = ((isLive || !secondaryUrl) && playbackUrl) ? playbackUrl : secondaryUrl;

	const tagText = () => {
		if (isOnDemand) {
			return t("On Demand");
		} else if (isBroadcast) {
			return t("Broadcast");
		} else if (isBreakoutRooms) {
			return t("Breakout Rooms");
		} else if (isFireside) {
			return t("Fireside");
		} else {
			return "";
		}
	};

	const tagIcon = () => {
		if (isOnDemand) {
			return ICONS.ON_DEMAND;
		} else if (isBroadcast) {
			return ICONS.VIEWERS_EYE;
		} else if (isBreakoutRooms) {
			return ICONS.BREAKOUT_ROOMS;
		} else if (isFireside) {
			return ICONS.FIRESIDE;
		} else {
			return "";
		}
	};

	const tagIconColor = () => {
		switch (template) {
			case TemplateClassNames["Apollo"]:
				return COLORS.WHITE;
			case TemplateClassNames["Studio 54"]:
			case TemplateClassNames["O2"]:
				return COLORS.BLACK;
			default:
				return COLORS.WHITE;
		}
	};

	const handleFiresidesHasStreams = useCallback((hasStreams: boolean) => {
		setIsFiresideLive(hasStreams);
		dispatch(setFiresideLive(hasStreams ? Date.now() : null));
	}, [dispatch]);

	const isTestStream = useMemo(() => {
		return !isEditor && session?.streaming_options?.test_stream && session.session_type === SessionTypesEnum.broadcast;
	}, [isEditor, session.streaming_options, session.session_type]);

	const renderSessionStream = () => {
		const [source, homepageVideo] = getHomepageVideo(session, language);

		const isOnDemandAndVideoExists = session.session_type === SessionTypesEnum.onDemand && source;
		const isBroadcastEventAndNotTesting = !isTestStream && session.session_type === SessionTypesEnum.broadcast;
		const isFiresideBroadcast = session.session_type === SessionTypesEnum.fireside;
		const isEmbedTypeAndEmbedExists = session.broadcast_type === EBroadcastTypes.embed && source;
		const replayOnAndExists = session.replay_on && secondaryUrl;
		const isLiveAndNotTesting = !isTestStream && isLive;

		if (attendeeInPersonModeEnabled) {
			return (
				<div
					className={
						classNames(
							"session-video-placeholder",
							'fireside-session',
							{
								[timestampStatus]: timestampStatus,
								'no-image': disableSessionThumbnails,
							}
						)
					}
					style={placeholderStyle}
				>
					<OptionalComponent display={isAdmin && disableSessionThumbnails}>
						<EditBannerColor />
					</OptionalComponent>
					<Suspense fallback="">
						<ErrorBoundary uniqueLabel="Fireside player">
							<FiresidesPlayer
								isEditor={!!isEditor}
								session={session}
								interacted={true}
								onHasStreams={handleFiresidesHasStreams}
								template={template}
								setDisableJoinStage={setDisableJoinStage}
								isFullscreen={firesideFullscreen}
							/>
						</ErrorBoundary>
					</Suspense>
					{!isFiresideLive && (
						<div className={classNames("live-in-card", "fireside-live-in-card", { "past-session": !isFutureSession })}>
							<OptionalComponent display={timestampStatus === ETimestampStatus.preLive}>
								<div
									className='live-in-card-fireside-content'
									// because the session banner uses position absolute, it's tricky to properly
									// get the title in position, so we need to use dynamic positioning
									ref={contentRef}
								>
									<Tag
										classNameTag="tag-container-type"
										text={t("Fireside")}
										icon={ICONS.FIRESIDE}
										iconColor={tagIconColor()}
									/>
									<TypographyItem
										className='live-in-card-session-title'
										tagName='h1'
									>
										{session?.title?.[languageProp] ?? session?.title?.base}
									</TypographyItem>
								</div>
							</OptionalComponent>
							<div className="live-in-card-text">
								<SessionCountdown
									timestamp={session.timestamp}
									end_timestamp={session.end_timestamp}
									replay_on={session.replay_on}
								/>
								<p>{startDate}</p>
							</div>
							{showAddToCalendarButton && (
								<Button
									onClick={openAddToCalendarModal}
									classButton="primary"
									template={template}
								>
									<span style={{ marginRight: '8px' }}>{t("ADD")}</span>
									<Icon name={ICONS.CALENDAR_ADD} color="" size={22} />
								</Button>
							)}
						</div>
					)}
					{/* TEMP inline style just to keep the button out of the way until we figure out where it's supposed to go */}
					<div style={{ position: 'absolute', bottom: !isLessThan640 ? '2%' : '5%', right: '1%' }}>
						<OptionalComponent display={isFiresideLive && !isFiresideHost}>
							<HandRaiseButton isAdmin={isAdmin} session={session} disableJoinStage={disableJoinStage} />
						</OptionalComponent>
						<OptionalComponent display={isFiresideLive}>
							<FiresideFullscreenButton isAdmin={isAdmin} isFullScreen={firesideFullscreen} goFullScreenCB={() => {
								setFiresideFullscreen(!firesideFullscreen);
							}} />
						</OptionalComponent>
						<OptionalComponent display={sessionChatEnabled && timestampStatus !== ETimestampStatus.preLive && (!isMobile/* && isLessThan640*/)}>
							<Button
								onClick={toggleChat}
								classButton={classNames('player-button chat-close waiting')}
								template={template}
								isDisabled={isAdmin}
							>
								{
									chatClosed ? (
										<Icon name={ICONS.CHAT_COMMENT} size={22} color={COLORS.WHITE} />
									) : (
										<Icon name={ICONS.CHAT_HIDE} size={22} color={COLORS.WHITE} />
									)
								}
							</Button>
						</OptionalComponent>
					</div>
				</div>
			);
		}

		if (
			(isOnDemandAndVideoExists || ((isBroadcastEventAndNotTesting || isFiresideBroadcast) && isEmbedTypeAndEmbedExists))
		) {
			// ondemand/embed player
			return (
				<div className={classNames("session-video-player-container")}>
					<Suspense fallback="">
						<ErrorBoundary uniqueLabel="On-demand or embed player">
							<Video
								video={homepageVideo as HomepageVideo}
								template={template}
								main={true}
								session={session}
								onRequestFullscreen={handleFullscreen}
								isFullscreen={isFullscreen}
								onChatClose={handleChatClose}
								chatClosed={chatClosed}
								hideChatButton={(!session.layout?.overlayChat && (isFullscreen && !isLessThan1024)) || isLandscapeIphone || moderatorView || !session?.session_chat_enabled}
								isOnDemand={session.session_type === SessionTypesEnum.onDemand}
								currentlyWatching={currentlyWatching}
								chatIsOverlaid={(session.layout?.overlayChat || forceOverlay) && !isLessThan640}
								displayCurrentlyWatching={displayCurrentlyWatching}
								colorTheme={session.video_player_controls_settings?.color_theme ?? 'default'}
								moderatorView={moderatorView}
								url={(moderatorView && playbackUrl) || undefined}
							/>
						</ErrorBoundary>
					</Suspense>
				</div>
			);
		}
		else if (isLiveAndNotTesting || replayOnAndExists) {
			// live player
			return (
				<div className={classNames("session-video-player-container")}>
					<Suspense fallback="">
						<ErrorBoundary uniqueLabel="Live player">
							<Video
								isAdmin={isAdmin}
								isFiresidesHost={isFiresideHost}
								session={session}
								loop={!!uploadedSecondaryVideo}
								url={broadcastUrl}
								template={template}
								main={true}
								onRequestFullscreen={handleFullscreen}
								isFullscreen={isFullscreen}
								onChatClose={handleChatClose}
								chatClosed={chatClosed}
								hideChatButton={(!session.layout?.overlayChat && (isFullscreen && !isLessThan1024)) || isLandscapeIphone || moderatorView || !session?.session_chat_enabled}
								currentlyWatching={currentlyWatching}
								isOnDemand={!isLive}
								chatIsOverlaid={(session.layout?.overlayChat || forceOverlay) && !isLessThan640}
								displayCurrentlyWatching={displayCurrentlyWatching}
								colorTheme={session.video_player_controls_settings?.color_theme ?? 'default'}
								moderatorView={moderatorView}
							/>
						</ErrorBoundary>
					</Suspense>
				</div>
			);
		}

		return (
			<div
				className={
					classNames(
						"session-video-placeholder",
						{
							[timestampStatus]: timestampStatus,
							'no-image': disableSessionThumbnails,
						}
					)
				}
				style={placeholderStyle}
			>
				<OptionalComponent display={isAdmin && disableSessionThumbnails}>
					<EditBannerColor />
				</OptionalComponent>
				{(session.session_type !== 'on-demand') && (
					<div className="session-live-card-container">
						<Tag
							classNameTag={classNames('session-live-card-container-tag')}
							text={tagText()}
							icon={tagIcon()}
							iconColor={tagIconColor()}
						/>
						<h1 className="session-live-card-title">{t(`landing:agenda.session.${session.session}.title`, session?.title[language] as string)}</h1>
						<div className={classNames("live-in-card", { "past-session": !isFutureSession })}>
							<div className="live-in-card-text">
								<SessionCountdown
									timestamp={session.timestamp}
									end_timestamp={session.end_timestamp}
									replay_on={session.replay_on}
								/>
								<p>{startDate}</p>
							</div>
							{showAddToCalendarButton && (
								<Button
									onClick={openAddToCalendarModal}
									classButton="primary"
									template={template}
								>
									<span style={{ marginRight: '8px' }}>{t("ADD")}</span>
									<Icon name={ICONS.CALENDAR_ADD} color="" size={22} />
								</Button>
							)}
						</div>
					</div>
				)}
				<OptionalComponent display={sessionChatEnabled && (timestampStatus !== ETimestampStatus.preLive || chatClosed)}>
					<Button
						onClick={toggleChat}
						classButton={classNames('player-button chat-close waiting')}
						template={template}
					>
						{
							chatClosed ? (
								<Icon name={ICONS.CHAT_COMMENT} size={22} color={COLORS.WHITE} />
							) : (
								<Icon name={ICONS.CHAT_HIDE} size={22} color={COLORS.WHITE} />
							)
						}
					</Button>
				</OptionalComponent>
			</div>
		);
	};

	if (session.session_type === SessionTypesEnum.breakoutRooms) {
		return null;
	}

	return (
		<div
			className={classNames("session-stream-wrapper", template, {
				'chat-closed': chatClosed || isLessThan640,
				'overlay-chat': session.layout?.overlayChat || forceOverlay,
				'fireside-session': isFireside,
				[timestampStatus]: isLive ? ETimestampStatus.live : timestampStatus,
				['supports-fullscreen']: isIphone && useiOSNativePlayer,
				'moderator-view': moderatorView,
				'dark-mode': session?.session_chat_dark_mode_enabled
			})}
			style={{ ...styleOverrides, ...(moderatorView ? { height: '100%' } : {}) }}
			ref={scrollContainer}
		>
			<div
				className={classNames('session-stream-container', { "attendee-in-person-enabled": attendeeInPersonModeEnabled })}>
				<div
					className={classNames("session-stream-container-inner", { center: session.layout?.overlayChat || forceOverlay }, { "attendee-in-person-enabled": attendeeInPersonModeEnabled }, { "greater-than-1024": !isLessThan1024 })}>
					<div
						className={classNames("session-stream", { "scrolled-past": hasScrolledPast && !isEditor && !isMobile && !isFullscreen }, { "attendee-in-person-enabled": attendeeInPersonModeEnabled }, { "less-than-1024": isLessThan1024 }, { "greater-than-640": !isLessThan640 })}
						ref={container}>
						{renderSessionStream()}
						{//for tablet, we force the overlay regardless of what the admin has selected (except for Firesides and iFrame broadcasts)
							(!mobileOnly && (session.layout?.overlayChat || forceOverlay)) && sessionChatEnabled && (
								<Suspense fallback="">
									<ErrorBoundary uniqueLabel="Live chat overlay">
										<LiveChat
											session={session}
											loading={!!loadingEventBundle}
											language={languageProp}
											registrationOn={!!eventBundle?.registration_on}
											blProfileUser={blProfileUser as BlProfile}
											template={template}
											forceOverlay={true}
											mobileOnly={false}
											onChatClose={handleChatClose}
											chatClosed={chatClosed}
											eventBundle={eventBundle}
											isAdmin={isAdmin}
											canGoLandscape={false}
										/>
									</ErrorBoundary>
								</Suspense>
							)
						}
					</div>
				</div>
				{//necessary to break these up to get the layouts correct
					(
						!session.layout?.overlayChat
						&& !forceOverlay
						&& !isFullscreen
					)
					&& !mobileOnly && sessionChatEnabled
					&& (
						<Suspense fallback="">
							<ErrorBoundary uniqueLabel="Live chat not overlay">
								<LiveChat
									session={session}
									loading={!!loadingEventBundle}
									language={languageProp}
									registrationOn={!!eventBundle?.registration_on}
									blProfileUser={blProfileUser as BlProfile}
									template={template}
									forceOverlay={false}
									mobileOnly={false}
									onChatClose={handleChatClose}
									chatClosed={chatClosed}
									eventBundle={eventBundle}
									isAdmin={isAdmin}
								/>
							</ErrorBoundary>
						</Suspense>
					)
				}
			</div >

			{!moderatorView &&
				<Suspense fallback="">
					<AddToCalendarModal
						session={session}
						eventName={eventBundle?.name || ""}
						open={openAddToCalendar}
						close={closeAddToCalendar}
						language={languageProp}
						isSingleSessionNoHomepage={eventBundle?.sessions?.length === 1 && !eventBundle?.homepage}
					/>
				</Suspense>
			}
		</div >
	);
};

export default SessionStream;
